/**
 * Live cross-tab / cross-app session resync for StudentPortal.
 *
 * A cookie flag alone can't update a tab that's already running. This listener
 * re-reads the shared cy-sso cookies whenever the tab regains focus / becomes
 * visible, and adopts them (via saveSession) when they carry a different token
 * than the one currently in localStorage.
 *
 * Concretely this is the "user switches profile in Portal, then returns to an
 * already-open Sandbox tab" fix — and the symmetric reverse. Portal is the
 * source of truth (it writes the cookies on every login/switch), so the open
 * tab simply trusts whatever the shared cookie now holds.
 */

import { decrypt } from "./other";
import { getCookie, isLoggedOut } from "./cookieUtils";
import { saveSession } from "./saveSession";
import { isNewerToken, tokenRole, tokenUserId } from "../services/tokenManager";
import { logSessionRecovered } from "../services/recoveryDiagnostics";
import sessionExpired from "../Core/sessionExpired";
import { LOGOUT_REASONS } from "./authEventLog";

const resyncFromCookies = () => {
  try {
    // Only a STUDENT session participates in cross-app auto-login/auto-logout
    // (symmetric with the student-only cookie WRITE in saveSession and the
    // student-only setLoggedOutFlag). A parent/staff session is app-local: it
    // must not adopt shared cookies, nor be torn down by a student's
    // cy_logged_out signal on a shared browser.
    let current = {};
    try {
      current = decrypt(localStorage.getItem("user")) || {};
    } catch {
      current = {};
    }
    if (current.role && current.role !== "student") return;

    // Cross-app logout: the other app raised `cy_logged_out`. If we still hold a
    // session here, end it now that the tab is back in focus.
    if (isLoggedOut()) {
      if (localStorage.getItem("user")) {
        sessionExpired(LOGOUT_REASONS.LOGGED_OUT_IN_OTHER_TAB, { trigger: "focus" });
      }
      return;
    }

    const accessToken = getCookie("Authorization");
    const refreshToken = getCookie("refresh-token");
    if (!accessToken || !refreshToken) return;

    // Only act when logged in here — don't auto-create a session on a public
    // page just because cookies linger (boot bootstrap owns first-paint login).
    if (!localStorage.getItem("user")) return;

    const localToken = current.ACCESS_TOKEN || current.Authorization;

    // Token unchanged → nothing to do (guards against loops).
    if (localToken === accessToken) return;

    // Non-student cookies are handoff transport (e.g. Epicenter one-click),
    // not a sync source — never adopt them into a running session.
    if (tokenRole(accessToken) !== "student") return;

    // Freshness guard: adopt only a token issued STRICTLY LATER than ours.
    // Every legitimate sync source (login, profile switch, refresh) mints a
    // brand-new token, so a not-newer cookie is stale by definition — adopting
    // it would downgrade a live session to rotated-away tokens.
    if (!isNewerToken(accessToken, localToken)) return;

    saveSession({ accessToken, refreshToken, userData: current });
    // Same student = the other app refreshed while this tab was away (a
    // recovery); another student = a profile switch, not counted.
    if (tokenUserId(accessToken) === tokenUserId(localToken)) {
      logSessionRecovered({ stage: "focus-resync", source: "cookie", staleToken: localToken });
    }
  } catch (_) {
    /* never throw from a focus handler */
  }
};

let registered = false;

export const registerAuthSyncListener = () => {
  if (registered || typeof window === "undefined") return;
  registered = true;

  window.addEventListener("focus", resyncFromCookies);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") resyncFromCookies();
  });
};

export default registerAuthSyncListener;
